/**
 * Phase 4 — AI / OCR multi-PO suggestions for Match invoice.
 * Suggestions are optional; the user confirms before orders are added.
 */

import {
  createMatchExtraOrderRow,
  getFilledMatchExtraOrders,
} from './invoiceMatchExtraOrdersHelpers'

function normalisePoKey(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
}

function dedupePurchaseOrderNumbers(values = []) {
  const seen = new Set()
  const result = []
  for (const value of values || []) {
    const purchaseOrderNumber = String(value || '').trim()
    if (!purchaseOrderNumber) continue
    const key = normalisePoKey(purchaseOrderNumber)
    if (seen.has(key)) continue
    seen.add(key)
    result.push(purchaseOrderNumber)
  }
  return result
}

/**
 * OCR multi-PO candidates from a table row or raw invoice document.
 */
export function getInvoiceOcrPurchaseOrderCandidates(invoice = {}) {
  if (Array.isArray(invoice.ocrPurchaseOrderCandidates)) {
    return dedupePurchaseOrderNumbers(invoice.ocrPurchaseOrderCandidates)
  }

  const ocr = invoice.ocr || {}
  if (Array.isArray(ocr.purchaseOrderCandidates)) {
    return dedupePurchaseOrderNumbers(ocr.purchaseOrderCandidates)
  }

  const rawCandidates = ocr.rawExtract?.purchaseOrderCandidates
  if (Array.isArray(rawCandidates)) {
    return dedupePurchaseOrderNumbers(rawCandidates)
  }

  return []
}

function hasSavedMultiPoAllocations(invoice = {}) {
  return (
    Array.isArray(invoice.poAllocations) &&
    invoice.poAllocations.filter(row =>
      String(row?.purchaseOrderNumber || '').trim(),
    ).length >= 2
  )
}

/**
 * Merge engine + OCR PO suggestions (deduped). Engine POs are listed first.
 */
export function collectMatchPoSuggestions(invoice = {}) {
  const suggestions = []
  const seen = new Set()

  const push = (purchaseOrderNumber, source) => {
    const po = String(purchaseOrderNumber || '').trim()
    if (!po) return
    const key = normalisePoKey(po)
    if (seen.has(key)) return
    seen.add(key)
    suggestions.push({ purchaseOrderNumber: po, source })
  }

  const enginePos = Array.isArray(invoice.matchedPurchaseOrderNumbers)
    ? invoice.matchedPurchaseOrderNumbers
    : []

  if (
    enginePos.length >= 2 ||
    String(invoice.matchingFailureReason || '').trim() ===
      'covers_multiple_orders'
  ) {
    enginePos.forEach(po => push(po, 'engine'))
  }

  getInvoiceOcrPurchaseOrderCandidates(invoice).forEach(po => push(po, 'ocr'))

  return suggestions
}

/**
 * Apply suggested POs to Match modal form state (primary + extra rows).
 */
export function applyMatchPoSuggestionsToForm(suggestions = [], invoice = {}) {
  const pos = (suggestions || [])
    .map(row => String(row?.purchaseOrderNumber || '').trim())
    .filter(Boolean)

  if (pos.length < 2) return null

  const [primaryPo, ...restPos] = pos
  const matchedOrderIds = Array.isArray(invoice.matchedOrderIds)
    ? invoice.matchedOrderIds.map(id => String(id))
    : []

  return {
    purchaseOrderNumber: primaryPo,
    linkedOrderId: invoice.linkedOrderId
      ? String(invoice.linkedOrderId)
      : matchedOrderIds[0] || null,
    extraOrders: restPos.map((po, index) =>
      createMatchExtraOrderRow({
        purchaseOrderNumber: po,
        linkedOrderId: matchedOrderIds[index + 1] || null,
      }),
    ),
  }
}

function formMatchesSuggestions(formState = {}, suggestions = []) {
  const primary = String(formState.purchaseOrderNumber || '').trim()
  const filledExtras = getFilledMatchExtraOrders(formState.extraOrders || [])
  const currentKeys = new Set(
    [primary, ...filledExtras.map(row => row.purchaseOrderNumber)]
      .filter(Boolean)
      .map(normalisePoKey),
  )
  const suggestedKeys = new Set(
    suggestions.map(row => normalisePoKey(row.purchaseOrderNumber)),
  )

  if (!suggestedKeys.size) return false
  if (currentKeys.size !== suggestedKeys.size) return false
  return [...suggestedKeys].every(key => currentKeys.has(key))
}

/**
 * Whether Match modal should show the multi-PO suggestion callout.
 */
export function getMatchPoSuggestionsPresentation(
  invoice = {},
  { purchaseOrderNumber = '', extraOrders = [], isCreditNote = false } = {},
) {
  if (isCreditNote || hasSavedMultiPoAllocations(invoice)) {
    return { show: false, suggestions: [], purchaseOrderNumbers: [] }
  }

  const suggestions = collectMatchPoSuggestions(invoice)
  if (suggestions.length < 2) {
    return { show: false, suggestions: [], purchaseOrderNumbers: [] }
  }

  if (
    formMatchesSuggestions({ purchaseOrderNumber, extraOrders }, suggestions)
  ) {
    return { show: false, suggestions: [], purchaseOrderNumbers: [] }
  }

  const engineCount = suggestions.filter(row => row.source === 'engine').length
  const ocrCount = suggestions.filter(row => row.source === 'ocr').length
  const count = suggestions.length
  const purchaseOrderNumbers = suggestions.map(row => row.purchaseOrderNumber)

  if (engineCount >= 2) {
    return {
      show: true,
      title: `We found ${count} orders that may match this invoice`,
      detail:
        String(invoice.matchingReasonDetail || '').trim() ||
        'Matching suggests this invoice covers several purchase orders. Review the list, remove any that do not apply, then allocate the total across them.',
      sourceLabel: 'From matching',
      buttonLabel: 'Add suggested orders',
      suggestions,
      purchaseOrderNumbers,
    }
  }

  if (ocrCount >= 2) {
    return {
      show: true,
      title: `We read ${count} purchase orders on this invoice`,
      detail:
        'These PO numbers were detected on the document. Add the ones that apply, remove any mistakes, then allocate the invoice total across them.',
      sourceLabel: 'From invoice scan',
      buttonLabel: 'Add scanned orders',
      suggestions,
      purchaseOrderNumbers,
    }
  }

  return {
    show: true,
    title: `We found ${count} orders to review`,
    detail:
      'Confirm which purchase orders this invoice covers before allocating.',
    sourceLabel: 'Suggested',
    buttonLabel: 'Add suggested orders',
    suggestions,
    purchaseOrderNumbers,
  }
}
